/**
 * Settings API client — user-scoped preferences and tenant-scoped settings.
 *
 * Backed by Agent L's endpoints:
 *   - PATCH /auth/me/preferences { theme_preference }
 *   - PATCH /tenant/settings    { brand_accent } (admin only)
 */

import client from './client';
import type { BrandAccent } from '@/data/brandSwatches';
import type { OnboardingState } from '@/types';

export type ThemePreference = 'light' | 'dark' | 'system';

export interface UserPreferencesPatch {
  theme_preference?: ThemePreference;
  /**
   * Coach-mark progress. When present, REPLACES the stored value wholesale —
   * the caller reads the current `seen` list, appends the just-finished tour
   * id, and PATCHes the full object back. See `useOnboarding`.
   */
  onboarding_state?: OnboardingState;
}

export interface TenantSettingsPatch {
  brand_accent?: BrandAccent;
}

/**
 * Persist a user-scoped preference (currently just `theme_preference`).
 * Returns the updated user payload from the server.
 */
export async function patchUserPreferences(
  prefs: UserPreferencesPatch
): Promise<unknown> {
  const res = await client.patch('/auth/me/preferences', prefs);
  return res.data;
}

/**
 * Persist a tenant-scoped setting (currently just `brand_accent`).
 * Admin-only on the backend; UI should gate accordingly.
 */
export async function patchTenantSettings(
  settings: TenantSettingsPatch
): Promise<unknown> {
  const res = await client.patch('/tenant/settings', settings);
  return res.data;
}
